// Utility functions shared between Dashboard and DomainDetails

// Field label mapping
export const fieldLabelsDashboard = {
  tags: 'Tags',
  source: 'Source',
  user: 'User',
  password: 'Password',
  risk_score: 'Risk',
  computer_type: 'Profile Type',
  added: 'Discovered',
  date: 'Infection Date',
  bucket: 'Bucket',
  domain_or_url: 'Domain/URL',
};

export const fieldLabelsDomainDetails = {
  tags: 'Tags',
  source: 'Source',
  user: 'User',
  password: 'Password',
  risk_score: 'Risk',
  computer_type: 'Profile Type',
  added: 'Discovered',
  date: 'Infection Date',
  bucket: 'Bucket',
  url: 'URL',
};

export const isDateField = (field) => field === 'date' || field === 'added';
export const isTagsField = (field) => field === 'tags';
export const isRiskScoreField = (field) => field === 'risk_score';
export const isComputerTypeField = (field) => field === 'computer_type';

// Risk score helper functions
export const getRiskScoreLabel = (score) => {
  if (!score || score < 1) return '-';
  if (score === 1) return 'Low';
  if (score === 2) return 'Medium';
  if (score === 3) return 'High';
  if (score === 4) return 'Very High';
  if (score >= 5) return 'Critical';
  return '-';
};

// Helper to convert hex to HSL
export function hexToHSL(hex) {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex.split('').map(x => x + x).join('');
  }
  const r = parseInt(hex.substr(0,2),16)/255;
  const g = parseInt(hex.substr(2,2),16)/255;
  const b = parseInt(hex.substr(4,2),16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h, s, l = (max + min) / 2;
  if(max === min){
    h = s = 0;
  }else{
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch(max){
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return { h: Math.round(h*360), s: Math.round(s*100), l: Math.round(l*100) };
}

// Helper to determine text color based on background lightness
export const getTextColorForBackground = (backgroundColor) => {
  // Handle HSL colors
  if (backgroundColor.startsWith('hsl')) {
    const match = backgroundColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (match) {
      const lightness = parseInt(match[3]);
      return lightness > 60 ? '#000000' : '#ffffff';
    }
  }
  // Handle hex colors
  if (backgroundColor.startsWith('#')) {
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }
  // Default to white for other color formats
  return '#ffffff';
};

export const getRiskScoreColor = (score) => {
  // Get the CSS variable
  const root = document.documentElement;
  let primaryColor = getComputedStyle(root).getPropertyValue('--primary-color').trim() || '#ffcc00';
  // Fallback for rgb/rgba: convert to hex if needed
  if (primaryColor.startsWith('rgb')) {
    const rgb = primaryColor.match(/\d+/g).map(Number);
    primaryColor = '#' + rgb.map(x => x.toString(16).padStart(2, '0')).join('');
  }
  const { h, s } = hexToHSL(primaryColor);
  // Define lightness for each score (1 = lightest, 5 = original)
  const lightness = {
    1: 95,
    2: 85,
    3: 70,
    4: 55
  };
  if (score >= 1 && score <= 4) {
    return `hsl(${h}, ${s}%, ${lightness[score]}%)`;
  }
  if (score === 5) {
    //red
    return 'hsl(0, 100%, 40%)';
  }
  return 'transparent';
};

export const getComputerTypeLabel = (type) => {
  if (!type || type === 'UNKNOWN') return 'Unknown';
  if (type === 'PERSONAL') return 'Personal';
  if (type === 'CORPORATE') return 'Corporate';
  if (type === 'ATTACKER') return 'Attacker';
  return 'Unknown';
};

export const getComputerTypeOptions = () => [
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'CORPORATE', label: 'Corporate' },
  { value: 'ATTACKER', label: 'Attacker' },
];

export function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  const pad = n => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function getUserRiskScore(riskScoreArr, userId) {
  if (Array.isArray(riskScoreArr)) {
    const userEntry = riskScoreArr.find(e => e.user_id === userId);
    if (userEntry) return userEntry.risk_score;
    const defaultEntry = riskScoreArr.find(e => e.user_id === 0);
    if (defaultEntry) return defaultEntry.risk_score;
    return undefined;
  }
  return riskScoreArr;
}

export function getUserComputerType(computerTypeArr, userId) {
  if (Array.isArray(computerTypeArr)) {
    const userEntry = computerTypeArr.find(e => e.user_id === userId);
    if (userEntry) return userEntry.computer_type;
    const defaultEntry = computerTypeArr.find(e => e.user_id === 0);
    if (defaultEntry) return defaultEntry.computer_type;
    return undefined;
  }
  return computerTypeArr;
} 